/**
 * Closed Authenticate Source presentation table. Copy is selected from typed
 * evidence only — protobuf codes, lockout, owned deadline, transport matcher,
 * or known cloud status — never from arbitrary server text.
 * Per rule: wire-contract-changes-must-not-break-old-firmware (ai.knowledge/global).
 */

import {
  isDirectLinkTransportFailure,
  DIRECT_LINK_UNREACHABLE_MESSAGE
} from './direct-link-transport-error.js';
import type { ElectronAPI } from '@electron-toolkit/preload';

/** Closed reason IDs from the DDA-M2 register for this dialog. */
export type DirectLinkAuthReasonId = 'R1' | 'R2' | 'R3' | 'R4' | 'R5' | 'R6' | 'R7' | 'R8' | 'R14';

/** Exact operation tokens this dialog may record. */
export type DirectLinkAuthOperation =
  | 'camera-auth'
  | 'camera-link-details'
  | 'camera-link-prepare'
  | 'camera-status'
  | 'auth-access-token'
  | 'cloud-link-agent'
  | 'unknown';

export type DirectLinkAuthEvidence = {
  operation: DirectLinkAuthOperation;
  /** Structured protobuf/API status when the value actually carries one. */
  code?: number | undefined;
  message?: string;
  lockoutLocked?: boolean;
  isTimeout?: boolean;
  timeoutMs?: number;
  /** False only when `window.electron.ipcRenderer.invoke` is absent. */
  hasDirectLinkIpc?: boolean;
  /** Typed Auth0/sign-in evidence (`login_required`), never inferred from message text. */
  signInRequired?: boolean;
  attemptReference: string;
};

export type DirectLinkAuthOutcome = {
  reasonId: DirectLinkAuthReasonId;
  message: string;
  operation: DirectLinkAuthOperation;
};

export const DIRECT_LINK_R1_REFUSED_MESSAGE =
  'The camera rejected these credentials. Check the username and password, then try again. Your other settings are kept.';

export const DIRECT_LINK_R3_MESSAGE = DIRECT_LINK_UNREACHABLE_MESSAGE;

export const DIRECT_LINK_R4_AUTH_MESSAGE =
  'The camera could not complete authentication. Try again. If it continues, ask your camera administrator for help.';

export const DIRECT_LINK_R4_PREPARE_MESSAGE =
  'The camera could not prepare a link to this Studio. Try again. If it continues, contact support.';

export const DIRECT_LINK_R4_GENERIC_MESSAGE =
  'Iris could not complete the camera connection request. Try again. If it continues, contact support.';

export const DIRECT_LINK_R5_SIGNIN_MESSAGE =
  'Your Iris sign-in needs attention. Sign in again, then retry linking this camera.';

export const DIRECT_LINK_R5_PERMISSION_MESSAGE =
  'You do not have permission to link this camera to this Studio. Ask a Studio owner to check your access.';

export const DIRECT_LINK_R6_MESSAGE =
  "The camera's link request is no longer available. Start the camera connection again to request a new link.";

export const DIRECT_LINK_R7_MESSAGE =
  'Authenticate this camera in the Iris desktop app. Open this Studio there, then select the camera again.';

export const DIRECT_LINK_UNREACHABLE_EVENT_MESSAGE = 'Camera was unreachable on the network.';

const GENERATE_LINK_FAILURE = 'Failed to generate link';
const AUTH_FAILURE = 'Failed run authentication with device';
const REQUEST_NOT_FOUND = 'agent request not found';
const REQUEST_EXPIRED = 'request has expired';

/** Seconds interpolated into R8; keeps fractional test budgets honest. Module-local: the two R8 builders below are its only callers. */
const directLinkTimeoutSeconds = (timeoutMs: number): number => timeoutMs / 1000;

export const DIRECT_LINK_R8_DETAILS_MESSAGE = (timeoutMs: number): string =>
  `The camera connection request did not finish within ${directLinkTimeoutSeconds(timeoutMs)} seconds. Check the camera's connection before trying again.`;

export const DIRECT_LINK_R8_CLOUD_MESSAGE = (timeoutMs: number): string =>
  `Linking the camera to the Studio did not finish within ${directLinkTimeoutSeconds(timeoutMs)} seconds. Check whether the camera is already linked before starting again.`;

export const DIRECT_LINK_R14_MESSAGE = (reference: string): string =>
  `Iris could not complete this step. Your settings are still here. Try again. If it continues, contact support with reference ${reference}.`;

/**
 * Indicates whether the desktop preload bridge is present AND exposes `invoke`,
 * which the direct-link request needs. A browser tab has no bridge at all; a
 * desktop build can expose one without this channel.
 *
 * The bridge is read through the `ElectronAPI` contract it is built from rather
 * than through the global `Window` augmentation, so the probe types identically
 * in every program that pulls this module in. The runtime guard is unchanged.
 */
export const hasDirectLinkIpcInvoke = (): boolean =>
  typeof (window as { electron?: ElectronAPI }).electron?.ipcRenderer?.invoke === 'function';

/**
 * Map typed evidence onto one closed reason. Unknown is an outcome, not a guessed cause.
 */
export const classifyDirectLinkAuthOutcome = (
  evidence: DirectLinkAuthEvidence
): DirectLinkAuthOutcome => {
  let operation = evidence.operation;
  const outcome = (reasonId: DirectLinkAuthReasonId, message: string): DirectLinkAuthOutcome => ({
    reasonId,
    message,
    operation
  });

  if (evidence.hasDirectLinkIpc === false) {
    return outcome('R7', DIRECT_LINK_R7_MESSAGE);
  }

  if (evidence.lockoutLocked) {
    return outcome('R2', '');
  }

  // Typed sign-in evidence only — never inferred from arbitrary error text.
  if (evidence.signInRequired) {
    return outcome('R5', DIRECT_LINK_R5_SIGNIN_MESSAGE);
  }

  // Token IO has no camera or link-service status contract. Only typed sign-in
  // evidence above or an owned timeout below can justify more specific advice.
  if (operation === 'auth-access-token' && !evidence.isTimeout) {
    return outcome('R14', DIRECT_LINK_R14_MESSAGE(evidence.attemptReference));
  }

  if (evidence.code === 401) {
    // The cloud link write carries the Studio's own authorization and never relays
    // camera credentials, so a cloud-link-agent 401 is a Studio sign-in problem —
    // telling the operator to re-check the camera password would be wrong. A
    // camera-facing 401 is the camera refusing those credentials, so it stays R1.
    if (operation === 'cloud-link-agent') {
      return outcome('R5', DIRECT_LINK_R5_SIGNIN_MESSAGE);
    }
    return outcome('R1', DIRECT_LINK_R1_REFUSED_MESSAGE);
  }

  if (evidence.code === 403 && operation === 'cloud-link-agent') {
    return outcome('R5', DIRECT_LINK_R5_PERMISSION_MESSAGE);
  }

  const message = evidence.message ?? '';
  const isCameraOperation =
    operation === 'camera-auth' ||
    operation === 'camera-link-details' ||
    operation === 'camera-link-prepare' ||
    operation === 'camera-status';

  // R6 is the cloud link-request expiry/not-found path, not a camera HTTP 404.
  if (
    operation === 'cloud-link-agent' &&
    (evidence.code === 404 ||
      message.includes(REQUEST_NOT_FOUND) ||
      message.includes(REQUEST_EXPIRED))
  ) {
    return outcome('R6', DIRECT_LINK_R6_MESSAGE);
  }

  if (evidence.isTimeout) {
    const timeoutMs = evidence.timeoutMs ?? 30_000;
    if (operation === 'cloud-link-agent' || operation === 'auth-access-token') {
      return outcome('R8', DIRECT_LINK_R8_CLOUD_MESSAGE(timeoutMs));
    }
    return outcome('R8', DIRECT_LINK_R8_DETAILS_MESSAGE(timeoutMs));
  }

  // Structured service status precedes transport text; a service response may
  // mention fetch failed without proving camera reachability failed.
  if (evidence.code === 500) {
    if (message.includes(GENERATE_LINK_FAILURE) || operation === 'camera-link-prepare') {
      operation = 'camera-link-prepare';
      return outcome('R4', DIRECT_LINK_R4_PREPARE_MESSAGE);
    }
    if (message.includes(AUTH_FAILURE) || operation === 'camera-auth') {
      operation = 'camera-auth';
      return outcome('R4', DIRECT_LINK_R4_AUTH_MESSAGE);
    }
    return outcome('R4', DIRECT_LINK_R4_GENERIC_MESSAGE);
  }

  if (isCameraOperation && evidence.code != null && evidence.code >= 400) {
    return outcome('R4', DIRECT_LINK_R4_GENERIC_MESSAGE);
  }

  // R3 is camera-facing transport only. Cloud/token fetch failures stay unknown.
  if (message && isCameraOperation && isDirectLinkTransportFailure(message)) {
    return outcome('R3', DIRECT_LINK_R3_MESSAGE);
  }

  return outcome('R14', DIRECT_LINK_R14_MESSAGE(evidence.attemptReference));
};
